import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Clock, LogIn, LogOut, RefreshCw, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }) : '-';

export const AttendanceTab = ({ employees = [] }) => {
  const [data, setData] = useState({ records: [], is_hr: false, own_employee_id: null });
  const [summary, setSummary] = useState(null);
  const [filterEmp, setFilterEmp] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [tsMonth, setTsMonth] = useState(new Date().toISOString().slice(0, 7));
  const [tsBusy, setTsBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterEmp !== 'all') params.employee_id = filterEmp;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API}/hrm/attendance`, { params });
      setData(res.data);
      if (res.data.is_hr) {
        const s = await axios.get(`${API}/hrm/attendance/summary`, { params: { date_from: dateFrom || undefined, date_to: dateTo || undefined } });
        setSummary(s.data);
      }
    } catch (e) {
      toast.error('Failed to load attendance');
    }
  }, [filterEmp, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const myOpen = data.records.find(r => r.employee_id === data.own_employee_id && r.open);

  const clock = async (action) => {
    setBusy(true);
    try {
      await axios.post(`${API}/hrm/attendance/${action}`, {});
      toast.success(action === 'clock-in' ? 'Clocked in' : 'Clocked out');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="attendance-tab">
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Clock className="h-5 w-5 text-primary" />
          <div className="flex-1 min-w-[160px]">
            <p className="font-semibold text-sm">My Clock Register</p>
            <p className="text-xs text-muted-foreground">
              {myOpen ? `Clocked in at ${fmtTime(myOpen.clock_in)} — don't forget to clock out` : 'Not clocked in today'}
            </p>
          </div>
          <Button size="sm" className="gap-1.5" disabled={busy || !!myOpen} onClick={() => clock('clock-in')} data-testid="clock-in-btn">
            <LogIn className="h-4 w-4" /> Clock In
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" disabled={busy || !myOpen} onClick={() => clock('clock-out')} data-testid="clock-out-btn">
            <LogOut className="h-4 w-4" /> Clock Out
          </Button>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-end gap-3">
        {data.is_hr && (
          <div className="space-y-1">
            <Label className="text-xs">Employee</Label>
            <Select value={filterEmp} onValueChange={setFilterEmp}>
              <SelectTrigger className="w-48" data-testid="attendance-employee-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" className="w-40" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="attendance-date-from" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" className="w-40" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="attendance-date-to" />
        </div>
        <Button size="sm" variant="ghost" onClick={load} className="gap-1"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
        {data.is_hr && (
          <div className="flex items-end gap-2 ml-auto">
            <div className="space-y-1">
              <Label className="text-xs">Timesheet month</Label>
              <Input type="month" className="w-40" value={tsMonth} onChange={e => setTsMonth(e.target.value)} data-testid="timesheet-month-input" />
            </div>
            <Button size="sm" variant="outline" className="gap-1.5" disabled={!tsMonth || tsBusy} data-testid="timesheet-pdf-btn"
              onClick={async () => {
                setTsBusy(true);
                try {
                  const res = await axios.get(`${API}/hrm/attendance/timesheet`, {
                    params: { month: tsMonth, employee_id: filterEmp !== 'all' ? filterEmp : undefined },
                    responseType: 'blob',
                  });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement('a');
                  a.href = url; a.download = `timesheet_${tsMonth}.pdf`; a.click();
                  URL.revokeObjectURL(url);
                  toast.success('Timesheet pack downloaded');
                } catch { toast.error('Timesheet generation failed'); }
                finally { setTsBusy(false); }
              }}>
              <FileText className="h-4 w-4" /> {tsBusy ? 'Generating…' : 'Timesheet PDF'}
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Clock In</TableHead>
                <TableHead>Clock Out</TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.records.map(r => (
                <TableRow key={r.id} data-testid={`attendance-row-${r.id}`}>
                  <TableCell className="font-medium text-sm">{r.employee_name}</TableCell>
                  <TableCell className="text-sm">{r.date}</TableCell>
                  <TableCell className="text-sm font-mono">{fmtTime(r.clock_in)}</TableCell>
                  <TableCell className="text-sm font-mono">{fmtTime(r.clock_out)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{r.hours != null ? r.hours.toFixed(2) : '-'}</TableCell>
                  <TableCell>
                    {r.missing_clock_out ? <Badge className="bg-red-100 text-red-700">Missing clock-out</Badge>
                      : r.open ? <Badge className="bg-blue-100 text-blue-700">Clocked in</Badge>
                        : <Badge className="bg-emerald-100 text-emerald-700">Complete</Badge>}
                    {(r.adjustments || []).length > 0 && <Badge variant="outline" className="ml-1 text-[10px]">adjusted</Badge>}
                  </TableCell>
                </TableRow>
              ))}
              {data.records.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No attendance records</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {data.is_hr && summary && summary.length > 0 && (
        <Card data-testid="attendance-summary-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">Attendance Summary</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">Days Present</TableHead>
                  <TableHead className="text-right">Total Hours</TableHead>
                  <TableHead className="text-right">Missing Clock-outs</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.map(s => (
                  <TableRow key={s.employee_id}>
                    <TableCell className="font-medium text-sm">{s.employee_name}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{s.days}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{s.total_hours.toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      {s.missing_clock_outs > 0 ? <Badge className="bg-red-100 text-red-700">{s.missing_clock_outs}</Badge> : <span className="text-sm text-muted-foreground">0</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
